import React, { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  FaBookOpen,
  FaExternalLinkAlt,
  FaImage,
  FaSortNumericDown,
} from "react-icons/fa";

const FALLBACK_IMG = "/images/commission-default.png";

const sanitize = (html = "") => DOMPurify.sanitize(html);

function htmlToText(html = "") {
  const clean = sanitize(html);
  const div = document.createElement("div");
  div.innerHTML = clean;
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
}

function shortText(text = "", n = 220) {
  const t = String(text || "").trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n)}…`;
}

function normalizeImageUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${window.location.origin}${trimmed}`;
  }

  return `${window.location.origin}/${trimmed}`;
}

export default function ReglementCard({ reglement }) {
  const [imgSrc, setImgSrc] = useState(
    normalizeImageUrl(reglement?.lien_url_image) || FALLBACK_IMG
  );

  const previewText = useMemo(() => {
    return shortText(htmlToText(reglement?.content || ""), 220);
  }, [reglement?.content]);

  const hasLink = !!String(reglement?.lien_url || "").trim();
  const orderValue =
    reglement?.display_order !== undefined && reglement?.display_order !== null
      ? reglement.display_order
      : "—";

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 w-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="relative h-48 w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
        <img
          src={imgSrc}
          alt={reglement?.title || "Règlement"}
          onError={() => setImgSrc(FALLBACK_IMG)}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />

        <div className="absolute left-4 top-4">
          {/* <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
            <FaSortNumericDown className="text-[11px]" />
            Ordre {orderValue}
          </div> */}
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
            {reglement?.lien_url_image ? <FaImage /> : <FaBookOpen />}
            {/* {reglement?.lien_url_image ? "Avec visuel" : "Document"} */}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div>
          <h3 className="text-lg font-bold leading-snug text-slate-900 dark:text-white line-clamp-2">
            {reglement?.title || "Règlement"}
          </h3>

          {previewText ? (
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300 line-clamp-5">
              {previewText}
            </p>
          ) : (
            <p className="mt-3 text-sm italic text-slate-400">
              Aucun contenu disponible pour le moment.
            </p>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {hasLink ? (
            <a
              href={reglement.lien_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200 hover:text-blue-400"
              title="Voir le règlement"
            >
              <FaExternalLinkAlt className="text-xs" />
              Consulter le règlement
            </a>
          ) : (
            <span className="inline-flex items-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              Aucun lien externe
            </span>
          )}
        </div>
      </div>
    </article>
  );
}